import React from 'react';
import EnhancedSEOHead from '../../components/EnhancedSEOHead';
import OptimizedHeroSection from './components/OptimizedHeroSection';
import ServicesPreview from './components/ServicesPreview';
import MarketIntelligence from './components/MarketIntelligence';
import PathwayCards from './components/PathwayCards';
import Header from '../../components/ui/UnifiedHeader';
import SharedFooter from '../../components/SharedFooter';

const HomepageStrategicCommandCenter = () => {
  const breadcrumbs = [
    { name: 'Home', url: '/' }
  ];

  // Enhanced schema data for homepage
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Bootminds - Strategic Command Center",
    "url": "https://bootminds.com",
    "description": "Bootminds helps global enterprises launch and expand India GCCs with transparent pricing, zero-risk engagement, and SkillStack™ staffing.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://bootminds.com/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    "mainEntity": {
      "@type": "Organization",
      "name": "Bootminds Elite",
      "legalName": "BootMinds Systems Pvt Ltd.",
      "alternateName": "Bootminds",
      "description": "Independent India GCC setup, expansion, executive hiring, and SkillStack staffing partner. Founded 2017 in Bangalore; relaunched January 2025 after the Intelliswift–LTTS exit.",
      "foundingDate": "2017",
      "foundingLocation": {
        "@type": "Place",
        "name": "Bangalore, India"
      },
      "founder": {
        "@type": "Person",
        "name": "Sanjay Jain",
        "jobTitle": "Founder & CEO",
        "sameAs": "https://www.linkedin.com/in/saanjayjain/"
      },
      "numberOfEmployees": "50-100",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "IN",
        "addressRegion": "Karnataka", 
        "addressLocality": "Bangalore"
      },
      "areaServed": [
        {
          "@type": "Country",
          "name": "India"
        }
      ],
      "serviceArea": {
        "@type": "Place",
        "name": "Global"
      }
    }
  };

  // LocalBusiness schemas for the two real offices — geographic anchors for AI engines
  const bangaloreOfficeSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": "https://bootminds.com/#bengaluru-office",
    "name": "Bootminds Elite — Bengaluru office",
    "parentOrganization": { "@type": "Organization", "name": "Bootminds Elite", "legalName": "BootMinds Systems Pvt Ltd." },
    "url": "https://bootminds.com/contact",
    "description": "India headquarters of Bootminds Elite — GCC setup, expansion, executive hiring, and SkillStack staffing.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "101-101, F2 Mahagony, Manyata Tech Park, Nagwara",
      "addressLocality": "Bengaluru",
      "addressRegion": "Karnataka",
      "postalCode": "560045",
      "addressCountry": "IN"
    },
    "geo": { "@type": "GeoCoordinates", "latitude": 13.0461, "longitude": 77.6213 },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    "areaServed": [{ "@type": "Country", "name": "India" }, { "@type": "Place", "name": "Asia-Pacific" }],
    "priceRange": "$$$"
  };

  const camarilloOfficeSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": "https://bootminds.com/#camarillo-office",
    "name": "Bootminds Elite — Camarillo office",
    "parentOrganization": { "@type": "Organization", "name": "Bootminds Elite", "legalName": "BootMinds Systems Pvt Ltd." },
    "url": "https://bootminds.com/contact",
    "description": "US office of Bootminds Elite — engagement and account management for North American clients.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1000 Paseo Camarillo, Suite 215",
      "addressLocality": "Camarillo",
      "addressRegion": "CA",
      "postalCode": "93010",
      "addressCountry": "US"
    },
    "geo": { "@type": "GeoCoordinates", "latitude": 34.2164, "longitude": -119.0376 },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "17:00"
    },
    "areaServed": [{ "@type": "Country", "name": "United States" }, { "@type": "Country", "name": "Canada" }],
    "priceRange": "$$$"
  };

  // FAQ Schema for homepage
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is a Global Capability Center (GCC)?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A Global Capability Center (GCC) is a strategic offshore operation established by multinational corporations to provide specialized services, innovation capabilities, and operational excellence, typically in cost-effective locations like India."
        }
      },
      {
        "@type": "Question",
        "name": "How long does GCC setup take with Bootminds?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our proven methodology enables GCC setup in 90-120 days from initial planning to operational launch, including legal setup, talent acquisition, infrastructure, and compliance requirements."
        }
      },
      {
        "@type": "Question",
        "name": "What makes Bootminds different from other GCC consultants?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We are former GCC leaders and IT services experts with multiple GCC programs led since 2018 and deep expertise in building top-performing GCCs with measurable ROI."
        }
      }
    ]
  };

  return (
    <>
      <EnhancedSEOHead
        title="Build GCCs That Drive Innovation | Bootminds"
        description="Transform your Global Capability Center into an innovation powerhouse—operational in weeks, not years, with zero talent gaps or cost overruns. 90-day launch guarantee."
        keywords="GCC setup 90 days, Global Capability Center fast launch, India talent acquisition, AI innovation lab, GCC cost savings, tech staffing on demand, elite developers India, global enterprise GCC consulting"
        canonicalUrl="/"
        breadcrumbs={breadcrumbs}
        schemaData={[schemaData, faqSchema, bangaloreOfficeSchema, camarilloOfficeSchema]}
        pageType="website"
        ogImage="/assets/images/White_logo_Bootminds.png"
      />
      
      <div className="min-h-screen bg-background page-first-load">
        <Header />
        <main className="pt-16 lg:pt-20 content-animate">
          <OptimizedHeroSection />
          <ServicesPreview />
          <MarketIntelligence />
          <PathwayCards />
        </main>
        <SharedFooter />
      </div>
    </>
  );
};

export default HomepageStrategicCommandCenter;